import { memo } from "react";
import cs from "classnames";

import { Checkbox, Stack } from "@braintrust/braintrust-ui-components";
import { Box, Divider } from "@braintrust/braintrust-ui-components";
import { useMediaQuery } from "@braintrust/braintrust-ui-components";
import { BidRatingCard } from "@js/apps/common/components/rating-card";
import { useIsNodeStaff } from "@js/apps/common/hooks/is-node-staff";
import { isPublicBidWithoutUser } from "@js/apps/jobs/apps/bids/helpers";
import type { EmployerBidListBid, Job } from "@js/types/jobs";

import { useBidListItem } from "../../hooks/bid-list-item";
import { ReactionsFeedbackField } from "../bid-reactions";
import { ExpandedItem } from "../expanded-item";
import { ResumeMatchSummary } from "../resume-match-summary";

import {
  BidItemGradingSummary,
  BidListItemAvatar,
  BidOverview,
  CTASection,
  QASection,
  ShowCommentsBtn,
  SkillsSection,
  UserDetails,
} from "./components";

import style from "./style.module.scss";

export type BidListItemProps = {
  bid: EmployerBidListBid;
  job: Job;
  selected: boolean;
  onCheckboxChange: (bidId: number) => void;
  index?: number;
  isInterviewingTab: boolean;
};

export const BidListItem = memo(
  ({
    bid,
    job,
    selected,
    index,
    onCheckboxChange,
    isInterviewingTab,
  }: BidListItemProps) => {
    const isMobile = useMediaQuery(420);

    const { linkToSidePanel, handleClickWithinItem, isBidExpanded, expandBid } =
      useBidListItem({ bid, isInterviewingTab, jobId: job.id });

    const handleCheckboxChange = () => {
      onCheckboxChange(bid.id);
    };

    const handleOnClick: React.MouseEventHandler<HTMLDivElement> = (ev) => {
      void handleClickWithinItem(ev);
    };

    const isStaff = useIsNodeStaff();
    const hasProfile = !isPublicBidWithoutUser(bid);

    const showSkills = !!bid.freelancer.skills.length;
    const isQASectionEmpty = !bid.application_answers.length;
    const showGradingSummary = bid.interview && isInterviewingTab;
    const showResumeMatchSummary =
      !showGradingSummary && !!bid.resume_smart_matching;
    const showQASection =
      !isQASectionEmpty && !showGradingSummary && !showResumeMatchSummary;

    return (
      <Box
        className={cs(style.itemWrapper, {
          [style.blackBorder]: bid.recommended_by_matcher,
          [style.greenBg]: bid.status === ENUMS.BidStatus.HIRED,
          [style.redBg]: bid.is_hidden,
          [style.selected]: selected,
        })}
        onClick={handleOnClick}
      >
        <Box
          className={cs(style.topSection, {
            [style.topSectionWithSkills]: showSkills,
          })}
          data-testid={`bid ${!bid.is_hidden ? "eligible" : "ineligible"}`}
          sx={{ pt: "var(--py)" }}
        >
          <Box
            className={style.userDetails}
            sx={{
              display: "flex",
              alignItems: "center",
              pl: "var(--px)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={selected}
                onChange={handleCheckboxChange}
                noMargin
                sx={{ mr: 1, ml: -1 }}
                slotProps={{
                  input: {
                    "aria-label": "Select talent",
                  },
                }}
              />
            </Box>
            <BidListItemAvatar bid={bid} jobRoleName={job.role.name} />
            <UserDetails bid={bid} job={job} isMobile={isMobile} />
          </Box>

          <Stack
            sx={{
              alignSelf: "center",
              pr: "var(--px)",
              pl: { xs: "var(--px)", md: 0 },
              alignItems: { xs: "start", md: "end" },
            }}
            className={style.socials}
          >
            <BidOverview bid={bid} job={job} isMobile={isMobile} />
          </Stack>

          {showSkills && (
            <Box className={style.skills}>
              <SkillsSection bid={bid} link={linkToSidePanel} />
            </Box>
          )}
        </Box>
        <Divider color="beige" style={{ marginBottom: "12px" }} />
        {showQASection && <QASection bid={bid} link={linkToSidePanel} />}
        {showGradingSummary && <BidItemGradingSummary bid={bid} />}
        {showResumeMatchSummary && !!bid.resume_smart_matching && (
          <ResumeMatchSummary
            resumeSummary={bid.resume_smart_matching}
            jobRoleName={job.role.name}
          />
        )}
        <Stack
          direction="row"
          sx={{
            flexWrap: { xs: "wrap", lg: "nowrap" },
            gap: 1,
            alignItems: "center",
            px: "var(--px)",
            mb: 1.5,
          }}
        >
          <ReactionsFeedbackField bid={bid} />
          <ShowCommentsBtn
            bidId={bid.id}
            isBidExpanded={isBidExpanded}
            expandBid={expandBid}
            commentsCount={bid.notes_count}
          />
          <CTASection
            bid={bid}
            job={job}
            link={linkToSidePanel}
            index={index}
          />
        </Stack>

        {isBidExpanded?.(bid.id) && <ExpandedItem id={bid.id} />}

        {isStaff && hasProfile && (
          <BidRatingCard
            freelancer={bid.freelancer}
            idvStatus={bid.freelancer_idv_status}
          />
        )}
      </Box>
    );
  },
);
